/**
 * Firebase AI Logic SDK Integration
 *
 * Enables client-side AI calls using Gemini models through Firebase.
 * Data residency: Uses Vertex AI backend in europe-west6 (Zurich) region.
 *
 * @see https://firebase.google.com/docs/vertex-ai/get-started
 */

import { getApp } from "firebase/app";
import {
  getAI,
  getGenerativeModel,
  GoogleAIBackend,
  VertexAIBackend,
  GenerativeModel,
} from "@firebase/ai";

// Swiss region for data residency compliance
const VERTEX_AI_LOCATION = "europe-west6"; // Zurich

// Available models
export const AI_MODELS = {
  GEMINI_2_5_FLASH: "gemini-2.5-flash",
  GEMINI_2_5_FLASH_LITE: "gemini-2.5-flash-lite",
  GEMINI_1_5_PRO: "gemini-1.5-pro",
  /** @deprecated Use GEMINI_2_5_FLASH instead — retired March 2026 */
  GEMINI_2_FLASH: "gemini-2.0-flash",
} as const;

export type AIModelId = (typeof AI_MODELS)[keyof typeof AI_MODELS];

// Lazy-initialized AI instance
let aiInstance: ReturnType<typeof getAI> | null = null;
let modelCache: Map<string, GenerativeModel> = new Map();

/**
 * Get the Firebase AI instance
 * Initializes with Vertex AI backend for Swiss data residency
 */
function getFirebaseAI() {
  if (!aiInstance) {
    const app = getApp();
    // Use Vertex AI backend for enterprise features and data residency
    aiInstance = getAI(app, {
      backend: new VertexAIBackend(VERTEX_AI_LOCATION),
    });
  }
  return aiInstance;
}

/**
 * Get a generative model for text generation
 * @param modelId - The model to use (default: gemini-2.5-flash)
 */
export function getModel(
  modelId: AIModelId = AI_MODELS.GEMINI_2_5_FLASH
): GenerativeModel {
  const cacheKey = modelId;

  if (!modelCache.has(cacheKey)) {
    const ai = getFirebaseAI();
    const model = getGenerativeModel(ai, { model: modelId });
    modelCache.set(cacheKey, model);
  }

  return modelCache.get(cacheKey)!;
}

/**
 * Generate text content using Firebase AI
 * @param prompt - The prompt to send to the model
 * @param modelId - Optional model override
 * @returns The generated text response
 */
export async function generateText(
  prompt: string,
  modelId: AIModelId = AI_MODELS.GEMINI_2_5_FLASH
): Promise<string> {
  const model = getModel(modelId);

  const result = await model.generateContent(prompt);
  const response = result.response;

  return response.text();
}

/**
 * Generate structured JSON content using Firebase AI
 * @param prompt - The prompt describing the desired output
 * @param modelId - Optional model override
 * @returns Parsed JSON response
 */
export async function generateJSON<T = unknown>(
  prompt: string,
  modelId: AIModelId = AI_MODELS.GEMINI_2_5_FLASH
): Promise<T> {
  const model = getModel(modelId);

  // Add JSON instruction to prompt
  const jsonPrompt = `${prompt}\n\nRespond with valid JSON only, no markdown or code blocks.`;

  const result = await model.generateContent(jsonPrompt);
  const response = result.response;
  const text = response.text();

  // Parse the JSON response
  try {
    return JSON.parse(text);
  } catch (error) {
    // Try to extract JSON from markdown code block
    const jsonMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/);
    if (jsonMatch) {
      return JSON.parse(jsonMatch[1].trim());
    }
    throw new Error(`Failed to parse AI response as JSON: ${text}`);
  }
}

/**
 * Stream text generation for real-time UI updates
 * @param prompt - The prompt to send
 * @param onChunk - Callback for each text chunk
 * @param modelId - Optional model override
 */
export async function streamText(
  prompt: string,
  onChunk: (text: string) => void,
  modelId: AIModelId = AI_MODELS.GEMINI_2_5_FLASH
): Promise<string> {
  const model = getModel(modelId);

  const result = await model.generateContentStream(prompt);
  let fullText = "";

  for await (const chunk of result.stream) {
    const chunkText = chunk.text();
    fullText += chunkText;
    onChunk(chunkText);
  }

  return fullText;
}

/**
 * Check if Firebase AI is properly configured
 */
export async function checkAIConfig(): Promise<{
  configured: boolean;
  location: string;
  error?: string;
}> {
  try {
    const model = getModel();
    // Try a simple generation to verify configuration
    await model.generateContent("Say 'AI is working' in one word.");
    return {
      configured: true,
      location: VERTEX_AI_LOCATION,
    };
  } catch (error) {
    return {
      configured: false,
      location: VERTEX_AI_LOCATION,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

export { GoogleAIBackend, VertexAIBackend };
